// ── Branding Zifek ───────────────────────────────────────────────────
// Source unique de l'identité visuelle (couleurs issues du logo Zifek).
// Les couleurs sont stockées dans la colonne `branding` (jsonb) de la
// table `zifek` et éditables depuis le superadmin.
//
// Le moteur convertit chaque teinte (hex) en une échelle de tokens
// (50 → 950) utilisée par tout le site via les variables CSS.

export interface BrandingColors {
  /** Bleu Zifek — couleur principale, boutons, navigation */
  primary: string;
  /** Bleu nuit — titres, états hover/actifs, éléments premium */
  primaryDark: string;
  /** Turquoise — accents, liens, badges */
  turquoise: string;
  /** Vert marocain — validation, éléments secondaires */
  green: string;
  /** Vert olive — décoration / zellige */
  olive: string;
  /** Orange terre cuite — accent, CTA secondaires */
  terracotta: string;
  /** Blanc cassé — fond principal */
  cream: string;
  /** Ivoire zellige — bordures, surfaces */
  ivory: string;
  /** Bleu très sombre — texte / contraste */
  navy: string;
}

export interface Branding {
  name: string;
  logo: string | null;
  colors: BrandingColors;
}

export const DEFAULT_BRANDING_COLORS: BrandingColors = {
  primary: '#155C9C',
  primaryDark: '#284875',
  turquoise: '#479CA7',
  green: '#356637',
  olive: '#718E50',
  terracotta: '#B36023',
  cream: '#F7F9F8',
  ivory: '#E3E2D9',
  navy: '#172C43',
};

export const DEFAULT_BRANDING: Branding = {
  name: 'ZIFEK',
  logo: null,
  colors: DEFAULT_BRANDING_COLORS,
};

export const BRANDING_COLOR_FIELDS: {
  key: keyof BrandingColors;
  label: string;
  hint: string;
}[] = [
  { key: 'primary', label: 'Bleu Zifek (primaire)', hint: 'Boutons, navigation' },
  { key: 'primaryDark', label: 'Bleu nuit', hint: 'Titres, survol des boutons' },
  { key: 'turquoise', label: 'Turquoise', hint: 'Accents, liens, badges' },
  { key: 'terracotta', label: 'Orange terre cuite', hint: 'Accent, CTA secondaires' },
  { key: 'green', label: 'Vert marocain', hint: 'Validation, éléments positifs' },
  { key: 'olive', label: 'Vert olive', hint: 'Décoration / zellige' },
  { key: 'cream', label: 'Blanc cassé', hint: 'Fond principal' },
  { key: 'ivory', label: 'Ivoire zellige', hint: 'Bordures, surfaces' },
  { key: 'navy', label: 'Bleu très sombre', hint: 'Texte / contraste' },
];

const COLOR_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
const LIGHT_FRACS = [0.95, 0.82, 0.66, 0.45, 0.22];
const DARK_FRACS = [0.86, 0.73, 0.6, 0.47, 0.35];
const CHROMA_FACTORS = [0.1, 0.2, 0.38, 0.62, 0.85, 1, 0.92, 0.8, 0.66, 0.52, 0.4];

const BG_L = [0.985, 0.962, 0.911, 0.86, 0.79, 0.71, 0.62, 0.52, 0.42, 0.32, 0.23];
const BG_C = [0.003, 0.006, 0.011, 0.01, 0.009, 0.008, 0.007, 0.006, 0.005, 0.004, 0.003];

const FG_L = [0.97, 0.935, 0.87, 0.78, 0.68, 0.575, 0.48, 0.4, 0.335, 0.31, 0.288];
const FG_C = [0.25, 0.35, 0.45, 0.55, 0.65, 0.78, 0.88, 0.96, 1, 1, 1];

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

export function isValidHex(value: string): boolean {
  return /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((value || '').trim());
}

function normalizeHex(value: string): string {
  let v = (value || '').trim();
  if (!v.startsWith('#')) v = `#${v}`;
  if (v.length === 4) {
    v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  }
  return v.toUpperCase();
}

/** Normalise un code hex (ajoute #, majuscules, étend la forme courte). */
export function normalizeHexClient(value: string): string {
  return normalizeHex(value);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const clean = (hex || '').replace('#', '');
  if (clean.length !== 6) return null;
  return {
    r: parseInt(clean.substring(0, 2), 16) / 255,
    g: parseInt(clean.substring(2, 4), 16) / 255,
    b: parseInt(clean.substring(4, 6), 16) / 255,
  };
}

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Convertit un hex sRGB en OKLCH (L, C, H). */
export function hexToOklch(hex: string): { l: number; c: number; h: number } {
  const rgb = hexToRgb(hex);
  if (!rgb) return { l: 0.5, c: 0, h: 0 };
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);

  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

  const c = Math.sqrt(A * A + B * B);
  let h = (Math.atan2(B, A) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { l: L, c, h };
}

function token(l: number, c: number, h: number): string {
  return `${l.toFixed(4)} ${c.toFixed(4)} ${h.toFixed(2)}`;
}

/** Échelle complète (50 → 950) à partir d'une couleur d'accent. */
function colorScale(prefix: string, hex: string): Record<string, string> {
  const safe = isValidHex(hex) ? normalizeHex(hex) : DEFAULT_BRANDING_COLORS.primary;
  const { l: baseL, c: baseC, h } = hexToOklch(safe);
  const d = 0.97 - baseL;
  const lightness = [
    ...LIGHT_FRACS.map((f) => baseL + d * f),
    baseL,
    ...DARK_FRACS.map((f) => baseL * f),
  ];
  const out: Record<string, string> = {};
  lightness.forEach((L, i) => {
    out[`${prefix}-${COLOR_STEPS[i]}`] = token(
      clamp(L, 0.05, 0.99),
      baseC * CHROMA_FACTORS[i],
      h,
    );
  });
  return out;
}

/** Échelle des fonds (50 = couleur claire, 950 = sombre). */
function backgroundScale(hex: string): Record<string, string> {
  const safe = isValidHex(hex) ? normalizeHex(hex) : DEFAULT_BRANDING_COLORS.cream;
  const { h } = hexToOklch(safe);
  const out: Record<string, string> = {};
  BG_L.forEach((L, i) => {
    out[`--background-${COLOR_STEPS[i]}`] = token(L, BG_C[i], h);
  });
  return out;
}

/** Échelle du texte (950 = couleur foncée de base). */
function foregroundScale(hex: string): Record<string, string> {
  const safe = isValidHex(hex) ? normalizeHex(hex) : DEFAULT_BRANDING_COLORS.navy;
  const { c: baseC, h } = hexToOklch(safe);
  const out: Record<string, string> = {};
  FG_L.forEach((L, i) => {
    out[`--foreground-${COLOR_STEPS[i]}`] = token(L, baseC * FG_C[i], h);
  });
  return out;
}

/** Toutes les variables CSS de la charte, prêtes à injecter. */
export function brandingCssVars(colors: BrandingColors): Record<string, string> {
  const dark = isValidHex(colors.primaryDark) ? normalizeHex(colors.primaryDark) : DEFAULT_BRANDING_COLORS.primaryDark;
  const { l, c, h } = hexToOklch(dark);
  return {
    ...colorScale('--primary', colors.primary),
    // 600 = bleu nuit du logo (utilisé pour les états hover/actifs)
    '--primary-600': token(l, c, h),
    ...colorScale('--accent', colors.terracotta),
    ...colorScale('--secondary', colors.turquoise),
    ...backgroundScale(colors.cream),
    ...foregroundScale(colors.navy),
    // Teintes décoratives supplémentaires (vert marocain / olive)
    '--brand-green': isValidHex(colors.green) ? normalizeHex(colors.green) : DEFAULT_BRANDING_COLORS.green,
    '--brand-olive': isValidHex(colors.olive) ? normalizeHex(colors.olive) : DEFAULT_BRANDING_COLORS.olive,
  };
}

/** Applique la charte sur <html> (override des variables par défaut). */
export function applyBranding(colors: BrandingColors): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const vars = brandingCssVars(colors);
  Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
}

/**
 * Normalise l'objet branding lu en base.
 * Accepte les clés historiques (navy, cream, turquoise, terracotta, …).
 */
export function parseBrandingColors(raw: unknown): BrandingColors {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const pick = (key: keyof BrandingColors): string => {
    const value = source[key];
    if (typeof value === 'string' && isValidHex(value)) return normalizeHex(value);
    return DEFAULT_BRANDING_COLORS[key];
  };
  return {
    primary: pick('primary'),
    primaryDark: pick('primaryDark'),
    turquoise: pick('turquoise'),
    green: pick('green'),
    olive: pick('olive'),
    terracotta: pick('terracotta'),
    cream: pick('cream'),
    ivory: pick('ivory'),
    navy: pick('navy'),
  };
}

/** Sérialise les couleurs pour le stockage (clés historiques conservées). */
export function serializeBrandingColors(colors: BrandingColors): BrandingColors {
  return {
    primary: normalizeHex(colors.primary),
    primaryDark: normalizeHex(colors.primaryDark),
    turquoise: normalizeHex(colors.turquoise),
    green: normalizeHex(colors.green),
    olive: normalizeHex(colors.olive),
    terracotta: normalizeHex(colors.terracotta),
    cream: normalizeHex(colors.cream),
    ivory: normalizeHex(colors.ivory),
    navy: normalizeHex(colors.navy),
  };
}